import { useMemo } from 'react';

/**
 * WaveformBars — Spotify/Soundcloud-style bar waveform built from a
 * track's structure analysis (no need for raw peak data).
 *
 * Strategy:
 *  - We sample N bars across the track's duration (more bars on wider players).
 *  - Each bar's height is the segment energy at that timestamp + a small
 *    deterministic wobble seeded from the trackId so the pattern is stable
 *    across renders but each track looks different.
 *  - Bars left of the playhead use the "played" colour, right use "unplayed".
 *  - Segment type tints the unplayed bars (drop/build/main) so the listener
 *    can still see the structure at a glance.
 */
const BARS = 160; // ~3px wide bars across a 600-700px player

// Deterministic 0-1 hash so each track gets its own micro-pattern
function seededWobble(seed, idx) {
  // Cheap xorshift-ish hash. Stable across renders.
  let h = (seed * 2654435761 + idx * 16807) | 0;
  h ^= h >> 13;
  h = (h * 1274126177) | 0;
  h ^= h >> 16;
  return ((h & 0xffff) / 0xffff); // 0..1
}

function hashTrackId(id) {
  if (!id) return 1;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = ((h << 5) - h + id.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

function findSegmentAt(structure, t) {
  if (!structure || structure.length === 0) return null;
  for (const s of structure) {
    if (t >= s.start && t < s.end) return s;
  }
  return structure[structure.length - 1];
}

function segmentTint(type) {
  switch (type) {
    case 'drop':    return 'rgba(37, 99, 255, 0.45)';   // brand blue
    case 'build':   return 'rgba(245, 158, 11, 0.45)';  // amber
    case 'main':    return 'rgba(59, 130, 246, 0.30)';  // softer blue
    default:        return 'rgba(148, 163, 184, 0.32)'; // slate
  }
}

export default function WaveformBars({ structure, duration, progress, trackId }) {
  const seed = useMemo(() => hashTrackId(trackId), [trackId]);

  const bars = useMemo(() => {
    if (!structure || structure.length === 0 || !duration) {
      // Flat fallback wave when analysis is missing
      return Array.from({ length: BARS }, (_, i) => {
        const w = seededWobble(seed, i);
        return { height: 25 + w * 35, tint: 'rgba(148,163,184,0.32)' };
      });
    }
    return Array.from({ length: BARS }, (_, i) => {
      const t = (i / (BARS - 1)) * duration;
      const seg = findSegmentAt(structure, t);
      const baseEnergy = seg?.energy ?? 30;
      const wobble = seededWobble(seed, i);    // 0..1
      const wobble2 = seededWobble(seed * 7, i); // second hash → micro-noise
      // Mix segment energy with a small organic noise on top so it doesn't
      // look like flat blocks. Energy is 0..100 in our analyser.
      const h = Math.min(98, Math.max(8, baseEnergy * 1.7 + (wobble - 0.5) * 28 + (wobble2 - 0.5) * 10));
      return { height: h, tint: segmentTint(seg?.type) };
    });
  }, [structure, duration, seed]);

  // Where the playhead sits in BAR-index space (0..BARS)
  const playedUpTo = (progress / 100) * BARS;

  return (
    <div className="absolute inset-0 flex items-center px-1 gap-[1px]">
      {bars.map((b, i) => {
        const played = i < playedUpTo;
        return (
          <div
            key={i}
            className="flex-1 rounded-[1px] transition-colors"
            style={{
              height: `${b.height}%`,
              backgroundColor: played ? '#2563FF' : b.tint,
              minWidth: 1,
            }}
          />
        );
      })}
    </div>
  );
}
